import { ICreatePaymentTermsAcceptanceDto } from "types/LegalDocuments/LegalDocuments";

import { publicApi } from ".";

interface IPaymentProviderResponse {
  provider: "FINIX" | "STRIPE";
  finixApplicationId?: string;
  finixMerchantId?: string;
}

interface IFinixPaymentRequest {
  orderId: string;
  /** finix.js token — or omit it and pass the paymentInstrumentId the card-fee quote created. */
  token?: string;
  paymentInstrumentId?: string;
  /** CLE-2798: the credit-card fee the patient saw and accepted (cents). Required when one applies. */
  creditCardFeeAmountInCents?: number | null;
  amountInCents: number;
  saveForFuture?: boolean;
  payRemainingPrice?: boolean;
  savePaymentMethod?: boolean;
  fraudSessionId?: string;
  termsAcceptance?: ICreatePaymentTermsAcceptanceDto;
}

// CLE-2798 — after tokenization, before the charge: what does this card cost?
export interface IFinixCardFeeQuoteRequest {
  orderId: string;
  token?: string;
  useSavedPaymentMethod?: boolean;
  amountInCents: number;
}

export interface IFinixCardFeeQuoteResponse {
  success: boolean;
  paymentInstrumentId: string;
  cardType: string | null;
  cardBrand: string | null;
  last4: string | null;
  feeApplies: boolean;
  feePercent: number;
  feeAmountInCents: number;
  totalAmountInCents: number;
  orderFeePercent: number;
}

interface IFinixPaymentResponse {
  success: boolean;
  paymentId: string;
  finixTransferId: string;
}

interface IFinixPayRemainingWithSavedCardRequest {
  orderId: string;
  amountInCents: number;
  creditCardFeeAmountInCents?: number | null;
  fraudSessionId?: string;
  termsAcceptance?: ICreatePaymentTermsAcceptanceDto;
}

interface IFinixSavedPaymentMethodResponse {
  data: {
    paymentMethodId: string;
    cardDetails: {
      brand: string;
      last4: string;
      expMonth?: number;
      expYear?: number;
    };
  } | null;
}

interface IFinixUpdateCardRequest {
  orderId: string;
  token: string;
}

interface IFinixUpdateCardResponse {
  paymentMethodId: string;
  retriedInstallments: number;
}

interface IFinixSplitAuthRequest {
  orderId: string;
  token1: string;
  amountInCents1: number;
  token2: string;
  amountInCents2: number;
  saveForFuture?: boolean;
  paymentPlan?: boolean;
  payRemainingPrice?: boolean;
  customAmount?: number;
  fraudSessionId?: string;
}

interface IFinixSplitAuthResponse {
  success: boolean;
  authorizationIds: [string, string];
  paymentInstrumentIds: [string, string];
  buyerIdentityId: string;
  // CLE-2798: per-card credit-card fee the holds include; shown before capture.
  creditCardFeesInCents?: [number, number];
  cardTypes?: [string | null, string | null];
  creditCardFeePercent?: number;
  // Present only on 422 responses when a specific card failed
  failedCard?: 1 | 2;
  reason?: string;
  message?: string;
}

interface IFinixSplitCaptureRequest {
  orderId: string;
  authorizationIds: [string, string];
  amounts: [number, number];
  paymentInstrumentIds: [string, string];
  acknowledgedCreditCardFeesInCents?: [number | null, number | null];
  paymentPlan?: boolean;
  preferredPaymentMethodIndex?: 0 | 1;
  saveForFuture?: boolean;
  userId?: string;
  termsAcceptance?: ICreatePaymentTermsAcceptanceDto;
}

interface IFinixSplitCaptureResponse {
  success: boolean;
  message: string;
}

interface IFinixSplitCancelRequest {
  orderId: string;
  authorizationIds: string[];
  failureReason?: string;
  userId?: string;
}

interface IFinixSplitCancelResponse {
  success: boolean;
  message: string;
}

const finixQueries = publicApi.injectEndpoints({
  endpoints: (builder) => ({
    getPaymentProvider: builder.query<IPaymentProviderResponse, string>({
      query: (orderId) => ({
        url: `/finix/orders/${orderId}/payment-provider`,
        method: "GET",
      }),
    }),
    createFinixPayment: builder.mutation<
      IFinixPaymentResponse,
      IFinixPaymentRequest
    >({
      query: ({
        orderId,
        token,
        paymentInstrumentId,
        creditCardFeeAmountInCents,
        amountInCents,
        saveForFuture,
        payRemainingPrice,
        savePaymentMethod,
        fraudSessionId,
        termsAcceptance,
      }) => ({
        url: `/finix/orders/${orderId}/payment`,
        method: "POST",
        body: {
          token,
          paymentInstrumentId,
          creditCardFeeAmountInCents,
          amountInCents,
          saveForFuture,
          payRemainingPrice,
          savePaymentMethod,
          fraudSessionId,
          termsAcceptance,
        },
      }),
    }),
    // CLE-2798 — card-fee quote: turns the token into a Payment Instrument, reads its card type and
    // returns the credit-card fee (if any) the patient must see before the charge.
    quoteFinixCardFee: builder.mutation<
      IFinixCardFeeQuoteResponse,
      IFinixCardFeeQuoteRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: `/finix/orders/${orderId}/card-fee-quote`,
        method: "POST",
        body,
      }),
    }),
    payFinixRemainingWithSavedCard: builder.mutation<
      IFinixPaymentResponse,
      IFinixPayRemainingWithSavedCardRequest
    >({
      query: ({
        orderId,
        amountInCents,
        creditCardFeeAmountInCents,
        fraudSessionId,
        termsAcceptance,
      }) => ({
        url: `/finix/orders/${orderId}/pay-remaining-with-saved-card`,
        method: "POST",
        body: {
          amountInCents,
          creditCardFeeAmountInCents,
          fraudSessionId,
          termsAcceptance,
        },
      }),
    }),
    getFinixSavedPaymentMethod: builder.query<
      IFinixSavedPaymentMethodResponse,
      string
    >({
      query: (orderId) => ({
        url: `/finix/orders/${orderId}/saved-payment-method`,
        method: "GET",
      }),
    }),
    updateFinixPaymentMethod: builder.mutation<
      IFinixUpdateCardResponse,
      IFinixUpdateCardRequest
    >({
      query: ({ orderId, token }) => ({
        url: `/finix/orders/${orderId}/update-card`,
        method: "POST",
        body: { token },
      }),
    }),
    createFinixSplitAuth: builder.mutation<
      IFinixSplitAuthResponse,
      IFinixSplitAuthRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: `/finix/orders/${orderId}/split-payment/authorize`,
        method: "POST",
        body,
      }),
    }),
    captureFinixSplitPayment: builder.mutation<
      IFinixSplitCaptureResponse,
      IFinixSplitCaptureRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: `/finix/orders/${orderId}/split-payment/capture`,
        method: "POST",
        body,
      }),
    }),
    cancelFinixSplitAuth: builder.mutation<
      IFinixSplitCancelResponse,
      IFinixSplitCancelRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: `/finix/orders/${orderId}/split-payment/cancel`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetPaymentProviderQuery,
  useCreateFinixPaymentMutation,
  useQuoteFinixCardFeeMutation,
  usePayFinixRemainingWithSavedCardMutation,
  useGetFinixSavedPaymentMethodQuery,
  useLazyGetFinixSavedPaymentMethodQuery,
  useUpdateFinixPaymentMethodMutation,
  useCreateFinixSplitAuthMutation,
  useCaptureFinixSplitPaymentMutation,
  useCancelFinixSplitAuthMutation,
} = finixQueries;
